import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from '../../components/tailwind/badge';
import Logout from './Logout';
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import { lowerNavBarList, routeData } from '../../routes/routesWithLogin/constant';
import { getFilteredRouteData } from '../../utils/entitlementsHelper';
import { getLegacyEntitlements, hasModule } from '../../utils/capabilitiesHelper';
import { selectTotalUnreadCount } from '../../store/conversationSlice';

const Navbar = ({ open = true }) => {
  const routePathLink = useLocation();
  const capabilities = useSelector((state) => state.capabilities.capabilities);
  const unreadCount = useSelector(selectTotalUnreadCount);
  
  // Convert capabilities to legacy entitlements for backward compatibility with route filtering
  const legacyEntitlements = getLegacyEntitlements(capabilities);
  const filteredRoutes = getFilteredRouteData(routeData, legacyEntitlements);

  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const hasCRM = hasModule(capabilities, 'CRM');
  const hasWhatsApp = hasModule(capabilities, 'WHATSAPP');
  const isWhatsappUser = !hasCRM && hasWhatsApp;
  const whatsappData = filteredRoutes.find(r => r.name === 'Whatsapp');

  return (
    <div className={cn("h-full bg-[hsl(217,33%,10%)] text-white flex flex-col flex-shrink-0", open ? "w-56" : "w-[54px]")}>
      <div
        className={cn(
          "flex-shrink-0 flex items-center gap-3 border-b border-white/10",
          open ? "h-[60px] px-4" : "h-[60px] px-2 justify-center"
        )}
      >
        <img
          src="/prodyt.png"
          alt="Prodyt"
          className={cn("rounded-full object-cover shrink-0", open ? "h-9 w-9" : "h-8 w-8")}
        />
        {open && (
          <span className="text-lg font-bold text-white truncate">Prodyt</span>
        )}
      </div>

      <nav className={cn("flex-1 px-3 py-2 space-y-1 overflow-y-auto", !open && "px-2")}>
        {/* Render CRM links */}
        {filteredRoutes.filter(data => !lowerNavBarList.includes(data.name) && data.name !== 'Whatsapp' && data.name !== 'SelfService').map((item) => {
          if (!item.navName) return null;
          const isActive = routePathLink.pathname === item.url || 
                           (item.url === '/home/dashboard' && routePathLink.pathname === '/') || 
                           (item.url !== '/' && routePathLink.pathname.startsWith(item.url + '/'));
          return (
            <NavItem
              key={item.id}
              to={item.url}
              icon={item.icon}
              label={item.navName}
              isActive={isActive}
              open={open}
            />
          );
        })}

        {/* Self Service Section */}
        {(() => {
          const ss = filteredRoutes.find(r => r.name === 'SelfService');
          if (!ss) return null;
          const isActiveParent = routePathLink.pathname.startsWith('/selfservice') || expanded[ss.id];
          return (
            <NavGroup
              id={ss.id}
              icon={ss.icon}
              label={ss.navName || 'Self Service'}
              expandedMap={expanded}
              toggleExpand={toggleExpand}
              childrenItems={ss.children}
              isActiveParent={isActiveParent}
              open={open}
              currentPath={routePathLink.pathname}
            />
          );
        })()}

        {/* WhatsApp Section */}
        {whatsappData && (
          isWhatsappUser ? (
            <div className="space-y-1">
              <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">WhatsApp Business</div>
              {whatsappData.children && whatsappData.children.map((item) => {
                const isActive = routePathLink.pathname === item.url || (item.url !== '/' && routePathLink.pathname.startsWith(item.url + '/'));
                const isChat = item.name === 'Chat' || item.navName === 'Chat';
                return (
                  <NavItem
                    key={item.id}
                    to={item.url}
                    icon={item.icon && React.cloneElement(item.icon, { className: 'h-5 w-5' })}
                    label={item.navName || item.name}
                    isActive={isActive}
                    open={open}
                    badge={isChat ? unreadCount : 0}
                  />
                );
              })}
            </div>
          ) : (
            <NavGroup 
              id={whatsappData.id}
              icon={whatsappData.icon}
              label={'WhatsApp'}
              expandedMap={expanded}
              toggleExpand={toggleExpand}
              childrenItems={whatsappData.children}
              isActiveParent={routePathLink.pathname.startsWith('/whatsapp') || expanded[whatsappData.id]}
              open={open}
              currentPath={routePathLink.pathname}
              badges={{ 'Chat': unreadCount }}
              rightElement={open ? (
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && !expanded[whatsappData.id] && (
                    <span className="bg-[hsl(158,48%,36%)] text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                  <Badge className="bg-[hsl(158,48%,36%)] text-white text-[10px] px-1.5 py-0 font-semibold">NEW</Badge>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", !!expanded[whatsappData.id] && "rotate-180")} />
                </div>
              ) : null}
            />
          )
        )}
      </nav>

      <div className="mt-auto flex-shrink-0 p-2 border-t border-white/10 space-y-0.5">
        {/* Settings Link */}
        {filteredRoutes.find(r => r.name === 'Setting') && (
          (() => {
            const s = filteredRoutes.find(r => r.name === 'Setting');
            const isActive = routePathLink.pathname === s?.url;
            return (
              <NavItem
                to={s?.url}
                icon={s?.icon}
                label={s?.navName || 'Settings'}
                isActive={isActive}
                open={open}
              />
            );
          })()
        )}

        {/* Logout */}
        <Logout
          id={filteredRoutes.find(r => r.name === 'Logout')?.id}
          color={routePathLink.pathname === filteredRoutes.find(r => r.name === 'Logout')?.url ? 'rgb(31 41 55)' : 'rgb(148 163 184)'}
          icon={filteredRoutes.find(r => r.name === 'Logout')?.icon && React.cloneElement(filteredRoutes.find(r => r.name === 'Logout')?.icon, { className: 'h-5 w-5' })}
          navName={filteredRoutes.find(r => r.name === 'Logout')?.navName || 'Logout'}
          open={open}
        />
      </div>
    </div>
  );
};

export default Navbar;
